'use client';
import React, { useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Search, RefreshCw, Terminal } from 'lucide-react';
import NotificationCenter from './NotificationCenter';

interface ControlTopbarProps {
  title: string;
  subtitle?: string;
  lastUpdated?: string;
  moduleOptions?: { label: string; href: string }[];
}

export default function ControlTopbar({ title, subtitle, lastUpdated, moduleOptions }: ControlTopbarProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1200);
  };

  return (
    <header className="h-[64px] bg-card border-b border-border flex items-center px-6 gap-4 shrink-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-600 text-foreground truncate">{title}</h1>
          {moduleOptions?.length ? (
            <select
              value={moduleOptions.find(option => pathname === option.href)?.href || ''}
              onChange={(e) => e.target.value && router.push(e.target.value)}
              aria-label="Select Workshop CMS module"
              className="text-[11px] font-500 badge-neutral px-2 py-1 rounded-full border border-border bg-muted text-foreground outline-none cursor-pointer max-w-[360px]"
            >
              {moduleOptions.map((option) => (
                <option key={option.href} value={option.href}>{option.label}</option>
              ))}
            </select>
          ) : subtitle ? (
            <span className="text-[11px] font-500 badge-neutral px-2 py-0.5 rounded-full hidden sm:inline-flex">
              {subtitle}
            </span>
          ) : null}
        </div>
        {lastUpdated && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Last updated: {lastUpdated}
          </p>
        )}
      </div>

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5 w-56 border border-border">
        <Search size={14} className="text-muted-foreground shrink-0" />
        <input
          type="text"
          placeholder="Search control..."
          autoComplete="off"
          suppressHydrationWarning
          className="bg-transparent text-sm text-foreground placeholder-muted-foreground outline-none flex-1 min-w-0"
        />
        <kbd className="text-[10px] bg-card border border-border text-muted-foreground px-1.5 py-0.5 rounded font-mono hidden lg:inline">⌘K</kbd>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleRefresh}
          className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 active:scale-95"
          title="Refresh dashboard data"
        >
          <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
        </button>
        <NotificationCenter scope="control" pollIntervalMs={30000} />
        <div className="w-px h-6 bg-border mx-1" />
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
          <Terminal size={14} className="text-primary" />
          <span className="text-[12px] font-600 text-primary hidden sm:inline">Control Admin</span>
        </div>
      </div>
    </header>
  );
}